<script setup lang="ts">
import { ref } from 'vue';

export type FilterTab = {
  name: string;
  value: string | number;
};

type Props = {
  tabs: FilterTab[];
  modelValue: string | number;
  size?: 'default' | 'small';
};

const props = defineProps<Props>();
const emit = defineEmits<{ 'update:modelValue': [value: string | number] }>();

function selectTab(value: string | number) {
  emit('update:modelValue', value);
}

// Drag-to-scroll com mouse (desktop). No toque, mantemos o scroll nativo.
const DRAG_THRESHOLD_PX = 5;
const containerRef = ref<HTMLElement | null>(null);
const isDragging = ref(false);

let startX = 0;
let startScrollLeft = 0;
let moved = false;

function onPointerDown(e: PointerEvent) {
  if (e.pointerType !== 'mouse' || !containerRef.value) return;

  isDragging.value = true;
  moved = false;
  startX = e.clientX;
  startScrollLeft = containerRef.value.scrollLeft;
}

function onPointerMove(e: PointerEvent) {
  if (!isDragging.value || !containerRef.value) return;

  const dx = e.clientX - startX;

  if (Math.abs(dx) > DRAG_THRESHOLD_PX) moved = true;

  containerRef.value.scrollLeft = startScrollLeft - dx;
}

function endDrag() {
  isDragging.value = false;
}

// Bloqueia o clique-fantasma que encerra um arrasto, sem afetar cliques limpos.
function onClickCapture(e: MouseEvent) {
  if (moved) {
    e.stopPropagation();
    e.preventDefault();
    moved = false;
  }
}
</script>

<template>
  <div
    ref="containerRef"
    class="filter-tabs"
    :class="{ 'is-dragging': isDragging }"
    @click.capture="onClickCapture"
    @pointerdown="onPointerDown"
    @pointerleave="endDrag"
    @pointermove="onPointerMove"
    @pointerup="endDrag"
  >
    <BaseFilterTabButton
      v-for="tab in props.tabs"
      :key="tab.value"
      :active="modelValue === tab.value"
      :label="tab.name"
      :size="props.size"
      @click="selectTab(tab.value)"
    />
  </div>
</template>

<style scoped>
.filter-tabs {
  display: flex;
  justify-content: start;
  overflow-x: auto;
  gap: 7.5px;
  scrollbar-width: none;
  -ms-overflow-style: none;
  -webkit-overflow-scrolling: touch;
  user-select: none;
  cursor: grab;
}

.filter-tabs.is-dragging {
  cursor: grabbing;
}

.filter-tabs::-webkit-scrollbar {
  display: none;
}

@media (max-width: 1023px) {
  .filter-tabs {
    flex-wrap: nowrap;
  }
}
</style>
